import { Link } from 'react-router-dom';

import { DataTable, type Column } from '../components/DataTable.tsx';
import { Pagination } from '../components/Pagination.tsx';
import { dateOnly, num } from '../lib/format.ts';
import { usePaginatedList } from '../lib/usePaginatedList.ts';

interface Row {
  id: string;
  billNo: string | null;
  externalBillNo: string | null;
  deptName: string | null;
  deptCode: string | null;
  type: string;
  status: string;
  plannedDate: string | null;
  createTime: string | null;
  counted: number;
  smartlinkBook: number;
  smartlinkVariance: number;
}

export function StockCountsListPage() {
  const list = usePaginatedList<Row>({
    endpoint: '/api/dashboard/stock-counts',
    defaultSortColumn: 'create_time',
    defaultSortDirection: 'DESC',
  });

  const columns: Column<Row>[] = [
    {
      key: 'bill_no',
      label: 'Count no.',
      className: 'mono',
      render: (r) => <Link to={`/stock-counts/${r.id}`}>{r.billNo ?? r.id}</Link>,
    },
    { key: 'external_bill_no', label: 'External no.', className: 'mono', render: (r) => r.externalBillNo ?? '—' },
    { key: 'dept', label: 'Dept', render: (r) => r.deptName ?? r.deptCode ?? '—' },
    { key: 'type', label: 'Type', render: (r) => r.type },
    { key: 'status', label: 'Status', render: (r) => r.status },
    { key: 'create_time', label: 'Created', render: (r) => dateOnly(r.createTime) },
    { key: 'counted', label: 'Counted', className: 'num', render: (r) => num(r.counted) },
    { key: 'smartlink_book', label: 'SL book', className: 'num', render: (r) => num(r.smartlinkBook) },
    {
      key: 'smartlink_variance',
      label: 'SL variance',
      className: 'num',
      render: (r) => (
        <span style={{ color: r.smartlinkVariance === 0 ? 'var(--text-muted)' : 'var(--text)' }}>
          {num(r.smartlinkVariance)}
        </span>
      ),
    },
  ];

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Stock counts</h1>
        <span className="main-subtitle">
          {list.data ? `${list.data.total} count${list.data.total === 1 ? '' : 's'} · SmartLink physical stocktakes` : ''}
        </span>
      </header>

      <div className="toolbar">
        <input
          placeholder="Search count no. or dept…"
          defaultValue={list.state.search}
          onChange={(e) => list.setSearch(e.target.value)}
        />
      </div>

      {list.error && <div className="error">{(list.error as Error).message}</div>}
      {list.isLoading && !list.data && <div className="loading">Loading…</div>}

      {list.data && (
        <>
          <DataTable
            columns={columns}
            rows={list.data.rows}
            rowKey={(r) => r.id}
            emptyMessage="No stock counts yet. They appear here once Redawn runs a stocktake in SmartLink."
          />
          <Pagination
            page={list.state.page}
            pageSize={list.state.pageSize}
            total={list.data.total}
            onPageChange={list.setPage}
          />
        </>
      )}
    </>
  );
}
